import React from "react";

/**
 * Standard Submission Modal for all forms.
 * @param {string} state - 'submitting', 'success', or 'error'
 * @param {function} onClose - Function to close the modal
 * @param {string} companyName - Name of the company to display in the success message
 * @param {string} phoneNumber - Phone number to display in the success message
 */
const SubmissionModal = ({ state, onClose, companyName, phoneNumber }) => {
    if (!state) return null;

    const formatDisplayPhone = (phone) => {
        if (!phone) return "";
        const digits = phone.replace(/\D/g, "");
        const last10 = digits.slice(-10);
        if (last10.length < 10) return phone; // Fallback if too short
        return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
    };

    const formattedPhone = formatDisplayPhone(phoneNumber);
    const rawPhoneDigits = phoneNumber ? phoneNumber.replace(/\D/g, "") : "";
    const telLink = rawPhoneDigits.length >= 10 ? `tel:+${rawPhoneDigits.length > 10 ? rawPhoneDigits : "1" + rawPhoneDigits}` : `tel:${rawPhoneDigits}`;

    const states = {
        submitting: {
            icon: (
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            ),
            title: "Submitting...",
            message: "Please wait while we process your request.",
            button: null,
        },
        success: {
            icon: (
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                </div>
            ),
            title: "Request Submitted!",
            message: (
                <>
                    Thank you for contacting <span className="font-semibold">{companyName}</span>.
                    We truly appreciate your trust in us. A member of our team will reach out to you as soon as possible.
                    <br /><br />
                    If you do not hear from us within your desired timeframe, please feel free to call us at{" "}
                    <a href={telLink} className="text-[#CA0712] font-bold hover:underline">
                        {formattedPhone}
                    </a>.
                    <br /><br />
                    Thank you,<br />
                    The <span className="font-semibold">{companyName}</span> Team
                </>
            ),
            button: "Done",
        },
        error: {
            icon: (
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                    <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                </div>
            ),
            title: "Submission Failed",
            message: "Something went wrong. Please try again later.",
            button: "Close",
        },
    };

    const current = states[state];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex justify-center items-center p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl text-center max-w-sm w-full">
                <div className="mb-4">{current.icon}</div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">{current.title}</h3>
                <div className="text-sm text-gray-500 mb-6">{current.message}</div>
                {current.button && (
                    <button
                        onClick={onClose}
                        type="button"
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#CA0712] text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:text-sm"
                    >
                        {current.button}
                    </button>
                )}
            </div>
        </div>
    );
};

export default SubmissionModal;
